<template>
  <div
    ref="hero"
    class="membership-country-hero-wrapper country-context flex fix-it"
    data-test-id="component-help-hero"
  >
    <div class="h-full pt-20 lg:h-auto w-full lg:w-auto relative lg:flex lg:container lg:pt-0">
      <BlocksHeroBreadcrumbs :breadcrumbs="breadcrumbs" class="breadcrumbs absolute top-4 left-4 md:top-8 md:left-8 lg:left-4 z-20" :class="{ 'animate': animatedHero, 'animation-finished': animationFinished }" />

      <div class="main-animation absolute top-0 left-0 w-full main-animation h-25 flex flex-col justify-between lg:h-full lg:static lg:w-1/2 lg:order-2" :class="{ 'animate': animatedHero, 'animation-finished': animationFinished }" @animationend="animationEndHandler">
        <div class="top w-full h-full flex">
          <div class="h-full w-1/3 bg-1" />
          <div class="h-full w-1/3 bg-2" />
          <div class="h-full w-1/3 bg-1" />
        </div>
        <div class="bottom w-full h-full hidden lg:flex">
          <div class="h-full w-1/3 bg-2" />
          <div class="h-full w-1/3 bg-3" />
          <div class="h-full w-1/3 bg-2" />
        </div>
      </div>

      <div class="hero-body container-to-md flex flex-col justify-center lg:w-1/2 lg:order-1 lg:pr-12 mt-16 mb:mt-25" :class="{ 'animate': animatedHero }">
        <span class="font-gt-america-extended uppercase text-smallest leading-4 -tracking-04-em mt-5 mb-3 text-middle text-center lg:text-left">{{ headline }}</span>
        <h1 class="uppercase font-druk text-display-10 md:text-display-25 leading-9 md:leading-22 tracking-02-em md:tracking-02-em mb-5 md:mb-8 mt-1 md:mt-8 lg:mt-0 text-center lg:text-left pb-14 md:pb-22">
          <span v-for="(text, index) in getTitle" class="inline-block overflow-hidden mr-2 last:mr-0"><span class="text inline-block">{{ text }}</span></span>
        </h1>
      </div>
    </div>
  </div>
</template>

<script>
import { isDarkOrLightByElement } from '@/utils/contrast-calculator'
import { COUNTRY_COOKIE_KEY } from '@/utils/constants'

export default {
  props: {
    title: {
      type: [String, Array],
      required: true,
      default: ''
    },
    headline: {
      type: String,
      default: '',
      required: false
    },
    breadcrumbs: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data () {
    return {
      breadcrumbsTextVariant: 'dark',
      mobileBreadcrumbsTextVariant: 'dark',
      animatedHero: false,
      animationFinished: false
    }
  },
  computed: {
    getTitle () {
      return Array.isArray(this.title) ? this.title : this.title.split(' ')
    },
    getBreadcrumbsTextVariant () {
      let variant = this.mobileBreadcrumbsTextVariant === 'light'
        ? 'text-white'
        : 'text-dark'

      variant += this.breadcrumbsTextVariant === 'light'
        ? ' lg:text-white'
        : ' lg:text-dark'

      return variant
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateTextVariant)
    this.updateTextVariant()

    this.lockScroll()
    this.$nextTick(() => {
      this.animatedHero = true
    })
    // add country code class, so that the hero pattern can be styled accordingly
    const countryCode = this.$cookies.get(COUNTRY_COOKIE_KEY)
    if (countryCode) {
      this.$refs.hero.classList.add(`country-context-${countryCode.toLowerCase()}`)
    } else {
      this.$refs.hero.classList.add('country-context-default')
    }
  },
  unmounted () {
    window.removeEventListener('resize', this.updateTextVariant)
  },
  methods: {
    updateTextVariant () {
      this.breadcrumbsTextVariant = isDarkOrLightByElement(this.$refs.hero.querySelector('.hero-body'))
      const bg1 = isDarkOrLightByElement(this.$refs.hero.querySelector('.bg-1'))
      const bg2 = isDarkOrLightByElement(this.$refs.hero.querySelector('.bg-2'))
      this.mobileBreadcrumbsTextVariant = (bg1 === 'dark' || bg2 === 'dark') ? 'dark' : 'light'
    },
    onActionTrigger () {
      this.$emit('action')
      this.$track.linkClicked('membershipCountryHeroCtaButton', 'cta', { linkText: this.cta })
    },
    animationEndHandler (animation) {
      if (!animation.animationName.includes('grow-top')) {
        return
      }

      setTimeout(() => {
        this.animationFinished = true
        this.unlockScroll()
      }, 600)
    },
    lockScroll () {
      if (this.$device.isDesktop) { return }
      window.document.body.style.overflow = 'hidden'
    },
    unlockScroll () {
      if (this.$device.isDesktop) { return }
      window.document.body.style.overflow = ''
    }
  }
}
</script>

<style lang="postcss" scoped>
.membership-country-hero-wrapper {
  min-height: 25vh;
  transition: min-height 1s cubic-bezier(0.76, 0, 0.24, 1);

  -webkit-transform: translateZ(0);
  backface-visibility: hidden;

  @screen lg {
    min-height: 360px !important;
    height: 360px;
  }
}

.breadcrumbs {
  opacity: 0;
  transition: opacity 0.8s 1.3s cubic-bezier(0.76, 0, 0.24, 1);

  @screen lg {
    transition: opacity 0.8s cubic-bezier(0.76, 0, 0.24, 1);
  }

  &.animate {
    opacity: 1;
  }
}

.hero-body {
    opacity: 1;
    position: relative;

  @screen lg {
    .text {
      transform: translateY(100%);
    }

    .content {
      opacity: 0;
      transition: opacity 0.8s cubic-bezier(0.76, 0, 0.24, 1);
    }
  }

  &.animate {
    @screen lg {
      .text {
        animation: slideIn .8s cubic-bezier(0.76, 0, 0.24, 1) forwards;
      }

      .content {
        opacity: 1;
      }
    }
  }
}

.country-context-default {
  .main-animation {
    .bg-1 {
      background-color: #794DFF;
    }
    .bg-2 {
      background-color: white;
    }
    .bg-3 {
      background-color: #794DFF;
    }
  }
}

.main-animation {
  height: calc(100vh - var(--navbar-height));
  transition: height 0.6s 1.3s cubic-bezier(0.76, 0, 0.24, 1);

  -webkit-transform: translateZ(0);
  backface-visibility: hidden;

  .bg-1 {
    background-color: var(--country-context-membership-top);
  }
  .bg-2 {
    background-color: var(--country-context-membership-back);
  }
  .bg-3 {
    background-color: var(--country-context-membership-bottom);
  }

  .top {
    height: 0;
  }
  .bottom {
    height: 0;
  }

  @screen lg {
    transition: none;
  }

  &.animation-finished {
    transition: none;
  }

  &.animate {
    height: 30vh;

    .top {
      animation: grow-top 0.8s 0.5s cubic-bezier(0.76, 0, 0.24, 1) forwards;
    }

    .bottom {
      animation: grow-bottom 0.5s 0s cubic-bezier(0.76, 0, 0.24, 1) forwards;
    }

    @screen lg {
        height: 100%;
        .top {
            animation: grow-top 0.8s 0s cubic-bezier(0.76, 0, 0.24, 1) forwards;
        }

        .bottom {
            animation: grow-bottom 0.8s 0s cubic-bezier(0.76, 0, 0.24, 1) forwards;
        }
    }
  }
}

@keyframes slideIn {
  0%   {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes grow-bottom {
  0%   {
    height: 0;
  }
  100% {
    height: 40%;
  }
}
@keyframes grow-top {
  0%   {
    height: 0;
  }
  100% {
    height: 60%;
  }
}
</style>
